import React, { useState, useCallback, useMemo } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import EditProductFilterModal from 'components/admin/common/modal/filter/EditProductFilterModal';
import useModalState from 'core/hooks/state/useModalState';
import { validateForm } from 'core/utils/validation';
import useCrud from 'core/hooks/api/useCrud';

import { Alert } from 'react-bootstrap';

const ProductFilter = () => {
    const {
        fetchData: data,
        fetchError: error,
        refetch,
        postData: createProductFilterData,
        postError: creatingError,
        postLoading: creating,
        putData: updateProductFilterData,
        putError: updatingError,
        putLoading: updating,
        deleteData: deleteProductFilterData,
        deleteLoading: deleting,
        deleteError: deletingError,
        clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.PRODUCT_FILTERS);

    const { fetchData: filterOptionsData } = useCrud(API_ENDPOINTS.FILTER_OPTIONS);
    const { fetchData: productData } = useCrud(API_ENDPOINTS.PRODUCTS);
    const { fetchData: filterData } = useCrud(API_ENDPOINTS.FILTERS);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    const handleCloseDeleteProductFilter = () => {
        setErrors({});
        clearServerErrors();
    };

    const handleCloseEditProductFilter = () => {
        setErrors({});
        clearServerErrors();
    };

    const { isOpen: isOpenDeleteProductFilter, modalData: productFilterToDelete, openModal: openDeleteProductFilter, closeModal: closedDeleteProductFilter } = useModalState({
        onClose: handleCloseDeleteProductFilter
    });

    const { isOpen: isOpenEditProductFilter, modalData: selectedProductFilter, openModal: openEditProductFilter, closeModal: closedEditProductFilter } = useModalState({
        onClose: handleCloseEditProductFilter
    });

    const validationRules = [
        { field: 'product_id', method: 'required' },
        { field: 'filter_option_id', method: 'required' }
    ];

    const handleModalSave = useCallback(async (updatedProductFilter) => {
        setSuccessMessage(null);
        clearServerErrors();
        const formErrors = validateForm(updatedProductFilter, validationRules);
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        try {
            if (updatedProductFilter.id) {
                await updateProductFilterData(updatedProductFilter.id, updatedProductFilter);
                setSuccessMessage("Filtro de producto actualizado con éxito.");
            } else {
                await createProductFilterData(updatedProductFilter);
                setSuccessMessage("Filtro de producto creado con éxito.");
            }
            closedEditProductFilter();
            setErrors({});
            refetch();
        } catch (error) {
            console.error(error);
        }
    }, [updateProductFilterData, createProductFilterData, refetch]);

    const handleCreateClick = useCallback(() => {
        setErrors({});
        const newProductFilter = {
            product_id: '',
            filter_option_id: ''
        };
        openEditProductFilter(newProductFilter);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        setSuccessMessage(null);
        if (productFilterToDelete) {
            try {
                await deleteProductFilterData(productFilterToDelete.id);
                setSuccessMessage("Filtro de producto eliminado con éxito.");
                refetch();
                closedDeleteProductFilter();
            } catch (error) {
                console.error(error);
            }
        }
    }, [productFilterToDelete, deleteProductFilterData, refetch]);

    const columns = useMemo(() => [
        { field: 'product_name', header: 'Producto', sortable: true },
        { field: 'filter_name', header: 'Filtro', sortable: true },
        { field: 'filter_option_value', header: 'Valor deL Filtro', sortable: true }
    ], []);

    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">Filtros de Producto</h5>
                        <button className="btn btn-primary" onClick={handleCreateClick} disabled={updating || creating}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Agregar Filtro de Producto
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            <DataTableWrapper
                data={data || []}
                columns={columns}
                onEdit={openEditProductFilter}
                onDelete={openDeleteProductFilter}
                paginator={false}
                totalRecords={data?.length || 0}
                emptyMessage="No se encontraron registros."
                sortField="product_id"
                sortOrder={1}
                lazy={false}
                showGridlines={true}
            />
            <EditProductFilterModal
                show={isOpenEditProductFilter}
                productFilter={selectedProductFilter}
                handleClose={closedEditProductFilter}
                handleSave={handleModalSave}
                updating={updating || creating}
                errors={errors}
                requestError={creatingError || updatingError}
                products={productData}
                filters={filterData}
                filtersOptions={filterOptionsData}
            />
            <ConfirmDeleteModal
                show={isOpenDeleteProductFilter}
                onHide={closedDeleteProductFilter}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                error={deletingError}
            />
        </div>
    );
};

export default React.memo(ProductFilter);